<template>
  <div class="gps-dashboard">
    <!-- Settings Alert -->
    <div v-if="!hasSettings" class="settings-alert">
      <i class="bi bi-exclamation-triangle"></i>
      <div>
        <strong>Catapult Integration Not Configured</strong>
        <p>Connect your Catapult OpenField account to view GPS stats.</p>
        <button @click="showSetupModal = true" class="btn btn-sm btn-primary">
          <i class="bi bi-gear me-1"></i>Configure
        </button>
      </div>
    </div>

    <!-- Date Filter & Actions -->
    <div class="date-filter" v-if="hasSettings">
      <div class="filter-group">
        <label>From</label>
        <input type="date" v-model="filters.from" @change="loadData" />
      </div>
      <div class="filter-group">
        <label>To</label>
        <input type="date" v-model="filters.to" @change="loadData" />
      </div>
      <div class="filter-group">
        <label>Type</label>
        <select v-model="filters.type" @change="loadData">
          <option value="">All</option>
          <option value="training">Training</option>
          <option value="match">Match</option>
        </select>
      </div>
      <div class="filter-actions">
        <button class="btn btn-primary btn-sm" @click="syncData" :disabled="syncing">
          <i :class="syncing ? 'bi bi-arrow-repeat spin' : 'bi bi-cloud-download'"></i>
          {{ syncing ? 'Syncing...' : 'Sync Data' }}
        </button>
        <button class="btn btn-outline btn-sm" @click="showSetupModal = true">
          <i class="bi bi-gear"></i>
        </button>
      </div>
    </div>

    <!-- Sync Status -->
    <div v-if="syncStatus" :class="['sync-status', syncStatus.type]">
      <i :class="syncStatus.type === 'success' ? 'bi bi-check-circle' : syncStatus.type === 'error' ? 'bi bi-exclamation-circle' : 'bi bi-info-circle'"></i>
      <span>{{ syncStatus.message }}</span>
      <button class="dismiss-btn" @click="syncStatus = null"><i class="bi bi-x"></i></button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading GPS data...</p>
    </div>

    <!-- Team Summary Cards -->
    <div v-else-if="summary" class="summary-cards">
      <div class="summary-card">
        <div class="card-icon bg-primary">
          <i class="bi bi-calendar3"></i>
        </div>
        <div class="card-content">
          <span class="value">{{ summary.session_count || 0 }}</span>
          <span class="label">Sessions</span>
        </div>
      </div>

      <div class="summary-card">
        <div class="card-icon bg-success">
          <i class="bi bi-geo-alt"></i>
        </div>
        <div class="card-content">
          <span class="value">{{ formatDistance(summary.avg_distance) }}</span>
          <span class="label">Avg Distance</span>
        </div>
      </div>

      <div class="summary-card">
        <div class="card-icon bg-warning">
          <i class="bi bi-lightning-charge"></i>
        </div>
        <div class="card-content">
          <span class="value">{{ formatNumber(summary.avg_player_load) }}</span>
          <span class="label">Avg Player Load</span>
        </div>
      </div>

      <div class="summary-card">
        <div class="card-icon bg-danger">
          <i class="bi bi-speedometer2"></i>
        </div>
        <div class="card-content">
          <span class="value">{{ formatSpeed(summary.peak_velocity) }}</span>
          <span class="label">Peak Velocity</span>
        </div>
      </div>

      <div class="summary-card">
        <div class="card-icon bg-info">
          <i class="bi bi-arrow-up-right"></i>
        </div>
        <div class="card-content">
          <span class="value">{{ formatDistance(summary.avg_sprint_distance) }}</span>
          <span class="label">Avg Sprint Distance</span>
        </div>
      </div>

      <div class="summary-card">
        <div class="card-icon bg-secondary">
          <i class="bi bi-people"></i>
        </div>
        <div class="card-content">
          <span class="value">{{ summary.athlete_count || 0 }}</span>
          <span class="label">Athletes Tracked</span>
        </div>
      </div>
    </div>

    <!-- Rankings Section -->
    <div v-if="rankings && !loading" class="rankings-section">
      <div class="section-header">
        <h3>Athlete Rankings</h3>
        <div class="metric-tabs">
          <button
            v-for="metric in availableMetrics"
            :key="metric.key"
            :class="['tab', { active: selectedMetric === metric.key }]"
            @click="selectedMetric = metric.key; loadRankings()"
          >
            {{ metric.label }}
          </button>
        </div>
      </div>

      <div class="rankings-table">
        <table>
          <thead>
            <tr>
              <th class="rank">#</th>
              <th class="athlete">Athlete</th>
              <th class="position">Position</th>
              <th class="value">Avg</th>
              <th class="value">Peak</th>
              <th class="sessions">Sessions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(athlete, index) in currentRankings" :key="athlete.athlete_id">
              <td class="rank">
                <span :class="['rank-badge', getRankClass(index)]">{{ index + 1 }}</span>
              </td>
              <td class="athlete">
                <span class="jersey" v-if="athlete.jersey_number">#{{ athlete.jersey_number }}</span>
                {{ athlete.first_name }} {{ athlete.last_name }}
              </td>
              <td class="position">{{ athlete.position_name || '-' }}</td>
              <td class="value">{{ formatMetricValue(athlete.avg_value) }}</td>
              <td class="value">{{ formatMetricValue(athlete.max_value) }}</td>
              <td class="sessions">{{ athlete.session_count }}</td>
            </tr>
            <tr v-if="!currentRankings.length">
              <td colspan="6" class="empty-state">No data available</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Session Selector -->
    <div v-if="activities.length && !loading" class="session-selector-section">
      <div class="session-selector-row">
        <label class="session-selector-label">
          <i class="bi bi-calendar3-event"></i>
          Select Session
        </label>
        <div class="session-select-wrapper">
          <select
            v-model="selectedActivityId"
            class="session-select"
            @change="onSessionSelect"
          >
            <option :value="null" disabled>Choose a session to view...</option>
            <optgroup v-if="matchActivities.length" label="Matches">
              <option
                v-for="activity in matchActivities"
                :key="activity.id"
                :value="activity.id"
              >
                {{ activity.activity_name }} - {{ formatDate(activity.start_time) }}
              </option>
            </optgroup>
            <optgroup v-if="trainingActivities.length" label="Training">
              <option
                v-for="activity in trainingActivities"
                :key="activity.id"
                :value="activity.id"
              >
                {{ activity.activity_name }} - {{ formatDate(activity.start_time) }}
              </option>
            </optgroup>
          </select>
          <i class="bi bi-chevron-down select-arrow"></i>
        </div>
        <span class="session-count">{{ activities.length }} sessions</span>
      </div>
    </div>

    <!-- Setup Modal -->
      <div v-if="showSetupModal" class="modal-overlay" @click.self="showSetupModal = false">
        <div class="setup-modal">
          <div class="modal-header">
            <h3><i class="bi bi-gear me-2"></i>Catapult Integration Setup</h3>
            <button class="close-btn" @click="showSetupModal = false">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="modal-body">
            <div class="setup-section">
              <h4>1. API Token</h4>
              <p>Generate an API token from your OpenField Cloud account to sync GPS data.</p>

              <div class="help-steps">
                <ol>
                  <li>Log in to <strong>OpenField Cloud</strong> (eu.openfield.catapultsports.com)</li>
                  <li>Go to <strong>Settings</strong> (gear icon, top right)</li>
                  <li>Click <strong>API Tokens</strong></li>
                  <li>Click <strong>Create Token</strong>, give it a name, and copy the token</li>
                  <li>Paste it below</li>
                </ol>
              </div>

              <div class="form-group">
                <label>API Token</label>
                <input
                  type="password"
                  v-model="settingsForm.api_token"
                  placeholder="Paste your API token here"
                />
                <small v-if="settingsForm.has_api_token && !settingsForm.api_token">
                  <i class="bi bi-check-circle text-success"></i> Token saved ({{ settingsForm.api_token_masked }})
                </small>
              </div>

              <div class="form-group">
                <label>Region</label>
                <select v-model="settingsForm.api_region">
                  <option value="eu">Europe / EMEA</option>
                  <option value="us">North America</option>
                  <option value="au">Asia-Pacific</option>
                </select>
                <small>Must match your OpenField Cloud region.</small>
              </div>
            </div>

            <div class="setup-section">
              <h4>2. Web Scraper Login (Alternative)</h4>
              <p>If you don't have an API token, log in with your OpenField Cloud credentials to sync data automatically.</p>

              <div class="form-group">
                <label>OpenField Username</label>
                <input
                  type="text"
                  v-model="settingsForm.scraper_username"
                  placeholder="Your OpenField Cloud username"
                />
              </div>

              <div class="form-group">
                <label>OpenField Password</label>
                <input
                  type="password"
                  v-model="settingsForm.scraper_password"
                  placeholder="Your OpenField Cloud password"
                />
                <small v-if="settingsForm.has_scraper_password && !settingsForm.scraper_password">
                  <i class="bi bi-check-circle text-success"></i> Password saved
                </small>
              </div>

              <div class="scraper-actions" style="margin-top: 0.75rem;">
                <button
                  class="btn btn-sm btn-primary"
                  @click="scraperLoginAndSync"
                  :disabled="scraperLoggingIn || (!settingsForm.scraper_username && !settingsForm.has_scraper_password)"
                >
                  <i :class="scraperLoggingIn ? 'bi bi-arrow-repeat spin' : 'bi bi-box-arrow-in-right'"></i>
                  {{ scraperLoggingIn ? 'Logging in...' : 'Login & Sync' }}
                </button>

              <!-- Sign-in health: a stale Catapult password used to be visible
                   only in server logs, so it went unnoticed for months while
                   the app ran on cached cookies. -->
              <div v-if="scraperSignInProblem" class="scraper-signin-alert" :class="{ paused: scraperSignInProblem.paused }">
                <i class="bi bi-exclamation-triangle-fill"></i>
                <div>
                  <strong>{{ scraperSignInProblem.paused ? 'Catapult sign-in paused' : 'Catapult sign-in is failing' }}</strong>
                  <p>{{ scraperSignInProblem.detail }}</p>
                  <p class="scraper-signin-alert__hint">
                    Check the Catapult username and password above, then use Login &amp; Sync.
                  </p>
                </div>
              </div>

                <span v-if="scraperStatusData" class="scraper-status-badge" :class="scraperStatusData.status">
                  <i :class="scraperStatusData.status === 'success' ? 'bi bi-check-circle' : scraperStatusData.status === 'expired' ? 'bi bi-exclamation-triangle' : 'bi bi-x-circle'"></i>
                  {{ scraperStatusData.message || scraperStatusData.status }}
                </span>
              </div>
            </div>

            <div class="setup-section">
              <h4>3. Webhook Configuration (Optional)</h4>
              <p>Optionally configure OpenField to push data automatically via webhook.</p>

              <div class="webhook-url-box" v-if="webhookUrl">
                <label>Your Webhook URL:</label>
                <div class="url-display">
                  <code>{{ webhookUrl }}</code>
                  <button @click="copyWebhookUrl" class="copy-btn">
                    <i class="bi bi-clipboard"></i>
                  </button>
                </div>
              </div>
              <button v-else @click="generateWebhookUrl" class="btn btn-secondary btn-sm">
                Generate Webhook URL
              </button>

              <div class="form-group" style="margin-top: 0.75rem;">
                <label>Webhook Secret (Optional)</label>
                <input
                  type="text"
                  v-model="settingsForm.webhook_secret"
                  placeholder="Enter secret for signature verification"
                />
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showSetupModal = false">Cancel</button>
            <button class="btn btn-primary" @click="saveSettings" :disabled="saving">
              {{ saving ? 'Saving...' : 'Save Settings' }}
            </button>
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import { catapultService } from '@/services/catapult-service.js'
import { mapGetters } from 'vuex'

export default {
  name: 'GPSTeamDashboard',

  props: {
    userData: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ['view-activity', 'view-all-activities'],

  data() {
    return {
      loading: false,
      saving: false,
      hasSettings: false,
      showSetupModal: false,
      webhookUrl: null,

      filters: {
        from: this.getDefaultFromDate(),
        to: this.getDefaultToDate(),
        type: ''
      },

      summary: null,
      rankings: {},
      activities: [],

      selectedMetric: 'total_distance',
      availableMetrics: [
        { key: 'total_distance', label: 'Distance', unit: 'm' },
        { key: 'total_player_load', label: 'Player Load', unit: '' },
        { key: 'sprint_distance', label: 'Sprint Distance', unit: 'm' },
        { key: 'max_velocity', label: 'Top Speed', unit: 'm/s' },
        { key: 'acceleration_count', label: 'High Accels', unit: '' }
      ],

      settingsForm: {
        webhook_secret: '',
        api_token: '',
        api_region: 'eu',
        has_api_token: false,
        api_token_masked: null
      },

      syncing: false,
      syncStatus: null,

      scraperLoggingIn: false,
      scraperStatusData: null,

      selectedActivityId: null
    }
  },

  computed: {
    ...mapGetters(['clubId']),
    currentRankings() {
      return this.rankings[this.selectedMetric] || []
    },
    matchActivities() {
      return this.activities.filter(a => a.activity_type === 'match')
    },
    trainingActivities() {
      return this.activities.filter(a => a.activity_type !== 'match')
    },

    /**
     * Summarise stored sign-in health into something worth showing.
     * Returns null when the last attempt succeeded or nothing has run.
     */
    scraperSignInProblem() {
      const h = this.scraperStatusData && this.scraperStatusData.login_health
      if (!h) return null
      if (!h.paused && !h.failure_count) return null

      const when = h.last_attempt_at ? new Date(h.last_attempt_at).toLocaleString() : 'recently'
      if (h.paused) {
        const until = new Date(h.locked_until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        return {
          paused: true,
          detail: `Automatic sign-in is paused until ${until} after ${h.failure_count} failed attempt(s). `
                + `Last error: ${h.last_error || 'unknown'}.`
        }
      }
      return {
        paused: false,
        detail: `${h.failure_count} failed sign-in attempt(s), last tried ${when}. `
              + `Last error: ${h.last_error || 'unknown'}.`
      }
    }
  },

  mounted() {
    if (this.clubId) {
      this.checkSettings()
      this.checkScraperStatus()
    }
  },

  methods: {
    getDefaultFromDate() {
      const date = new Date()
      date.setDate(date.getDate() - 30)
      return date.toISOString().split('T')[0]
    },

    getDefaultToDate() {
      return new Date().toISOString().split('T')[0]
    },

    async checkSettings() {
      try {
        const { data } = await catapultService.getSettings(this.clubId)
        this.hasSettings = !!(data.settings || data.data?.settings)
        const settings = data.settings || data.data?.settings
        if (settings) {
          this.settingsForm = { ...this.settingsForm, ...settings }
          this.loadData()
        }
      } catch (error) {
        console.error('Failed to check settings:', error)
      }
    },

    async loadData() {
      if (!this.hasSettings) return

      this.loading = true
      try {
        const params = {
          from: this.filters.from,
          to: this.filters.to,
          type: this.filters.type || undefined
        }

        const [summaryRes, activitiesRes] = await Promise.all([
          catapultService.getTeamSummary(this.clubId, params),
          catapultService.getActivities(this.clubId, params)
        ])

        this.summary = summaryRes.data.averages || summaryRes.data.data?.averages
        this.activities = activitiesRes.data.activities || activitiesRes.data.data?.activities || []

        // Load all rankings
        await this.loadRankings()
      } catch (error) {
        console.error('Failed to load GPS data:', error)
      } finally {
        this.loading = false
      }
    },

    async loadRankings() {
      try {
        const { data } = await catapultService.getRankings(this.clubId, this.selectedMetric, {
          from: this.filters.from,
          to: this.filters.to
        })
        this.rankings[this.selectedMetric] = data.rankings || data.data?.rankings || []
      } catch (error) {
        console.error('Failed to load rankings:', error)
      }
    },

    async generateWebhookUrl() {
      try {
        const { data } = await catapultService.getWebhookUrl(this.clubId)
        this.webhookUrl = data.webhook_url || data.data?.webhook_url
      } catch (error) {
        console.error('Failed to generate webhook URL:', error)
      }
    },

    copyWebhookUrl() {
      navigator.clipboard.writeText(this.webhookUrl)
    },

    async saveSettings() {
      this.saving = true
      try {
        const formData = {
          webhook_secret: this.settingsForm.webhook_secret,
          api_region: this.settingsForm.api_region
        }
        // Only send api_token if user entered a new one
        if (this.settingsForm.api_token) {
          formData.api_token = this.settingsForm.api_token
        }
        await catapultService.updateSettings(this.clubId, formData)
        this.hasSettings = true
        this.showSetupModal = false
        this.settingsForm.api_token = '' // Clear from memory
        await this.checkSettings()
      } catch (error) {
        console.error('Failed to save settings:', error)
      } finally {
        this.saving = false
      }
    },

    async syncData() {
      this.syncing = true
      this.syncStatus = { type: 'info', message: 'Connecting to Catapult OpenField Cloud...' }
      try {
        const { data } = await catapultService.syncFromApi(this.clubId, {
          from: this.filters.from,
          to: this.filters.to
        })
        if (data.success) {
          const s = data.summary || {}
          this.syncStatus = {
            type: 'success',
            message: `Sync complete: ${s.athletes_synced || 0} athletes, ${s.activities_synced || 0} activities, ${s.stats_synced || 0} stat records imported.`
          }
          this.loadData()
        } else {
          this.syncStatus = { type: 'error', message: data.message || 'Sync failed. Check your API token.' }
        }
      } catch (error) {
        console.error('Sync failed:', error)
        const msg = error.response?.data?.message || 'Failed to sync data. Check your API token and try again.'
        this.syncStatus = { type: 'error', message: msg }
      } finally {
        this.syncing = false
      }
    },

    formatDistance(meters) {
      if (!meters) return '-'
      if (meters >= 1000) {
        return (meters / 1000).toFixed(1) + ' km'
      }
      return Math.round(meters) + ' m'
    },

    formatSpeed(mps) {
      if (!mps) return '-'
      const kmh = mps * 3.6
      return kmh.toFixed(1) + ' km/h'
    },

    formatNumber(num) {
      if (!num) return '-'
      return Number(num).toFixed(1)
    },

    formatMetricValue(value) {
      if (!value) return '-'
      const metric = this.availableMetrics.find(m => m.key === this.selectedMetric)
      if (this.selectedMetric.includes('velocity') && !this.selectedMetric.includes('band')) {
        return this.formatSpeed(value)
      }
      if (this.selectedMetric.includes('distance')) {
        return this.formatDistance(value)
      }
      return Number(value).toFixed(1)
    },

    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      })
    },

    getRankClass(index) {
      if (index === 0) return 'gold'
      if (index === 1) return 'silver'
      if (index === 2) return 'bronze'
      return ''
    },

    async scraperLoginAndSync() {
      this.scraperLoggingIn = true
      this.scraperStatusData = null
      try {
        // Step 1: Login via Playwright
        const loginResult = await catapultService.scraperLogin(this.clubId, {
          scraper_username: this.settingsForm.scraper_username,
          scraper_password: this.settingsForm.scraper_password,
          api_region: this.settingsForm.api_region
        })

        if (!loginResult.data.success) {
          let errMsg = loginResult.data.message || 'Login failed'
          if (loginResult.data.debug_output) {
            console.error('Scraper debug output:', loginResult.data.debug_output)
          }
          // `blocked` means the server refused to attempt a sign-in, not that
          // one failed. Repeated presses with a stale password are what lock
          // the Catapult account, so show the stand-down and the way out
          // rather than inviting another click.
          this.scraperStatusData = {
            status: loginResult.data.blocked ? 'blocked' : 'error',
            message: errMsg,
            hint: loginResult.data.hint || null,
          }
          return
        }

        this.scraperStatusData = { status: 'success', message: 'Logged in, syncing data...' }

        // Step 2: Sync data using the captured token
        this.syncing = true
        const syncResult = await catapultService.scraperSync(this.clubId, {
          from: this.filters.from,
          to: this.filters.to
        })

        if (syncResult.data.success) {
          const s = syncResult.data.summary || {}
          this.syncStatus = {
            type: 'success',
            message: `Sync complete: ${s.athletes_synced || 0} athletes, ${s.activities_synced || 0} activities, ${s.stats_synced || 0} stat records imported.`
          }
          this.hasSettings = true
          this.showSetupModal = false
          this.loadData()
        } else {
          this.syncStatus = { type: 'error', message: syncResult.data.message || 'Sync failed.' }
        }

        this.scraperStatusData = { status: 'success', message: 'Login and sync complete' }
      } catch (error) {
        console.error('Scraper login/sync failed:', error)
        const msg = error.response?.data?.message || 'Scraper login failed. Check credentials and try again.'
        this.scraperStatusData = { status: 'error', message: msg }
      } finally {
        this.scraperLoggingIn = false
        this.syncing = false
      }
    },

    onSessionSelect() {
      if (!this.selectedActivityId) return
      const activity = this.activities.find(a => a.id === this.selectedActivityId)
      if (activity) {
        this.$emit('view-activity', activity)
        this.selectedActivityId = null
      }
    },

    async checkScraperStatus() {
      try {
        const { data } = await catapultService.scraperStatus(this.clubId)
        if (data.success) {
          this.scraperStatusData = data.scraper_status
        }
      } catch (error) {
        console.error('Failed to check scraper status:', error)
      }
    }
  }
}
</script>

<style scoped>
.gps-dashboard {
  padding: 0;
}

/* ========================================
   Settings Alert (dark)
   ======================================== */
.settings-alert {
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.settings-alert i {
  font-size: 1.5rem;
  color: #ffc107;
}

.settings-alert p {
  margin: 0.25rem 0 1rem;
  color: var(--text-muted, #adb5bd);
}

/* ========================================
   Date Filter
   ======================================== */
.date-filter {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-group label {
  font-size: 0.75rem;
  color: var(--text-muted, #6c757d);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.filter-group input,
.filter-group select {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color, #404258);
  border-radius: 6px;
  background: var(--bg-secondary, #2d3142);
  color: var(--color-text, #e8eaed);
  font-size: 0.9rem;
}

.filter-group input:focus,
.filter-group select:focus {
  outline: none;
  border-color: var(--primary, #667eea);
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
}

.btn-sm {
  padding: 0.4rem 0.8rem;
  font-size: 0.85rem;
}

.spin {
  animation: spin 1s linear infinite;
}

/* ========================================
   Sync Status (dark)
   ======================================== */
.sync-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.sync-status.success {
  background: rgba(40, 167, 69, 0.15);
  color: #5dd67a;
  border: 1px solid rgba(40, 167, 69, 0.3);
}

.sync-status.error {
  background: rgba(220, 53, 69, 0.15);
  color: #f27d8a;
  border: 1px solid rgba(220, 53, 69, 0.3);
}

.sync-status.info {
  background: rgba(23, 162, 184, 0.15);
  color: #5dd6e8;
  border: 1px solid rgba(23, 162, 184, 0.3);
}

.dismiss-btn {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  font-size: 1.1rem;
  color: currentColor;
}

.dismiss-btn:hover {
  opacity: 1;
}

/* ========================================
   Help Steps (dark)
   ======================================== */
.help-steps {
  background: var(--bg-tertiary, #252838);
  border: 1px solid var(--border-color, #404258);
  border-radius: 8px;
  padding: 0.75rem 1rem 0.75rem 1.5rem;
  margin-bottom: 1rem;
}

.help-steps ol {
  margin: 0;
  padding-left: 1rem;
}

.help-steps li {
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
  color: var(--text-muted, #adb5bd);
}

.text-success {
  color: #5dd67a;
}

/* ========================================
   Loading & Spinner
   ======================================== */
.loading-state {
  text-align: center;
  padding: 3rem;
  color: var(--text-muted, #adb5bd);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color, #404258);
  border-top-color: var(--primary, #667eea);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ========================================
   Summary Cards
   ======================================== */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.summary-card {
  background: var(--bg-secondary, #2d3142);
  border: 1px solid var(--border-color, #404258);
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: border-color 0.15s ease;
}

.summary-card:hover {
  border-color: var(--primary, #667eea);
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
  flex-shrink: 0;
}

.bg-primary { background: var(--primary, #667eea); }
.bg-success { background: #28a745; }
.bg-warning { background: #ffc107; color: #333 !important; }
.bg-danger { background: #dc3545; }
.bg-info { background: #17a2b8; }
.bg-secondary { background: #6c757d; }

.card-content {
  display: flex;
  flex-direction: column;
}

.card-content .value {
  font-size: 1.25rem;
  font-weight: 600;
}

.card-content .label {
  font-size: 0.75rem;
  color: var(--text-muted, #6c757d);
}

/* ========================================
   Rankings Section
   ======================================== */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-header h3 {
  margin: 0;
  font-size: 1.1rem;
}

.metric-tabs {
  display: flex;
  gap: 0.25rem;
}

.metric-tabs .tab {
  padding: 0.35rem 0.75rem;
  border: 1px solid var(--border-color, #404258);
  background: transparent;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--text-muted, #adb5bd);
}

.metric-tabs .tab:hover {
  border-color: var(--primary, #667eea);
  color: var(--color-text, #e8eaed);
}

.metric-tabs .tab.active {
  background: var(--primary, #667eea);
  color: white;
  border-color: var(--primary, #667eea);
}

.rankings-table {
  background: var(--bg-secondary, #2d3142);
  border: 1px solid var(--border-color, #404258);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 2rem;
}

.rankings-table table {
  width: 100%;
  border-collapse: collapse;
}

.rankings-table th,
.rankings-table td {
  padding: 0.75rem 1rem;
  text-align: left;
}

.rankings-table th {
  background: var(--bg-tertiary, #252838);
  font-size: 0.75rem;
  text-transform: uppercase;
  color: var(--text-muted, #6c757d);
}

.rankings-table tr:not(:last-child) td {
  border-bottom: 1px solid var(--border-color, #404258);
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 0.8rem;
  font-weight: 600;
  background: var(--bg-tertiary, #252838);
}

.rank-badge.gold { background: #ffd700; color: #333; }
.rank-badge.silver { background: #c0c0c0; color: #333; }
.rank-badge.bronze { background: #cd7f32; color: white; }

.jersey {
  background: var(--primary, #667eea);
  color: white;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  font-size: 0.75rem;
  margin-right: 0.5rem;
}

.empty-state {
  text-align: center;
  color: var(--text-muted, #6c757d);
  padding: 2rem !important;
}

/* ========================================
   Session Selector Dropdown
   ======================================== */
.session-selector-section {
  margin-bottom: 2rem;
}

.session-selector-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.session-selector-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-text, #e8eaed);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.session-selector-label i {
  color: var(--primary, #667eea);
}

.session-select-wrapper {
  position: relative;
  flex: 1;
  max-width: 500px;
}

.session-select {
  width: 100%;
  padding: 0.65rem 2.5rem 0.65rem 1rem;
  background: var(--bg-secondary, #2d3142);
  border: 1px solid var(--border-color, #404258);
  border-radius: 8px;
  color: var(--color-text, #e8eaed);
  font-size: 0.9rem;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  transition: border-color 0.15s ease;
}

.session-select:hover {
  border-color: var(--primary, #667eea);
}

.session-select:focus {
  outline: none;
  border-color: var(--primary, #667eea);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
}

.session-select option,
.session-select optgroup {
  background: #2d3142;
  color: #e8eaed;
}

.session-select optgroup {
  font-weight: 600;
  font-style: normal;
  color: var(--primary, #667eea);
}

.select-arrow {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted, #6c757d);
  pointer-events: none;
  font-size: 0.85rem;
}

.session-count {
  font-size: 0.8rem;
  color: var(--text-muted, #6c757d);
  white-space: nowrap;
}

/* ========================================
   Setup Modal (dark)
   ======================================== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(4px);
}

.setup-modal {
  background: #2d3142;
  color: #e8eaed;
  border-radius: 16px;
  border: 1px solid #404258;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
}

.setup-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #404258;
  color: #e8eaed;
}

.setup-modal .modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #e8eaed;
}

.setup-modal .close-btn {
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  opacity: 0.6;
  color: #adb5bd;
}

.setup-modal .close-btn:hover {
  opacity: 1;
  color: #e8eaed;
}

.setup-modal .modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.setup-section {
  margin-bottom: 2rem;
}

.setup-section:last-child {
  margin-bottom: 0;
}

.setup-section h4 {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  color: #e8eaed;
}

.setup-section > p {
  color: #adb5bd;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  font-size: 0.85rem;
  margin-bottom: 0.35rem;
  color: #adb5bd;
}

.form-group input[type="text"],
.form-group input[type="email"],
.form-group input[type="password"],
.form-group select {
  width: 100%;
  padding: 0.6rem 0.8rem;
  border: 1px solid #404258;
  border-radius: 8px;
  background: #252838;
  color: #e8eaed;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #667eea;
}

.form-group small {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #6c757d;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: #adb5bd;
}

.webhook-url-box {
  margin-bottom: 1rem;
}

.webhook-url-box label {
  color: #adb5bd;
}

.url-display {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.url-display code {
  flex: 1;
  background: #252838;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  overflow-x: auto;
  color: #7c8ef5;
  border: 1px solid #404258;
}

.copy-btn {
  padding: 0.5rem;
  background: #252838;
  border: 1px solid #404258;
  border-radius: 6px;
  cursor: pointer;
  color: #adb5bd;
}

.copy-btn:hover {
  border-color: #667eea;
  color: #e8eaed;
}

.setup-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #404258;
}

/* ========================================
   Buttons
   ======================================== */
.btn {
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
}

.btn-primary {
  background: var(--primary, #667eea);
  color: white;
}

.btn-primary:hover {
  background: #5a72d4;
}

.btn-secondary {
  background: var(--bg-secondary, #2d3142);
  border: 1px solid var(--border-color, #404258);
  color: var(--color-text, #e8eaed);
}

.btn-outline {
  background: transparent;
  border: 1px solid var(--border-color, #404258);
  color: var(--color-text, #e8eaed);
}

.btn-outline:hover {
  border-color: var(--primary, #667eea);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ========================================
   Scraper Status Badges (dark)
   ======================================== */
.scraper-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.scraper-signin-alert {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  margin-bottom: 12px;
  border-radius: 8px;
  border: 1px solid #f5c2c7;
  background: #fff5f5;
  color: #842029;
}

.scraper-signin-alert.paused {
  border-color: #ffe69c;
  background: #fff9e6;
  color: #664d03;
}

.scraper-signin-alert i {
  font-size: 1.1rem;
  line-height: 1.4;
}

.scraper-signin-alert p {
  margin: 4px 0 0;
  font-size: 0.86rem;
}

.scraper-signin-alert__hint {
  opacity: 0.85;
}

.scraper-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.scraper-status-badge.success {
  background: rgba(40, 167, 69, 0.2);
  color: #5dd67a;
}

.scraper-status-badge.error {
  background: rgba(220, 53, 69, 0.2);
  color: #f27d8a;
}

.scraper-status-badge.expired {
  background: rgba(255, 193, 7, 0.2);
  color: #ffd54f;
}
</style>
